import { createMachine, assign } from 'xstate'
import { StepDefinition } from '../types'
import { ExternalIntegration } from 'api/clients/ExternalIntegrationsClient'

export type FlowCtx = {
  initialState?: string
  skippedFrom?: string | null
  redirectTo?: string
  [key: string]: any
  websites?: string[]
  externalIntegrations?: ExternalIntegration[]
  brandKitId?: number
  brandVisualEnhancementId?: number
  brandPlanId?: number
  language?: string
  brandProfileEnhancementEnabled?: boolean
  isBrandPlanStrategiesEnabled?: boolean
  wantsToEnhance?: boolean
  isExistingUser?: boolean
  hasBusinessWebsite?: boolean
  isIntentionalBrandKit?: boolean
}

type BackTransition = string | Array<{ target: string; cond?: (context: FlowCtx) => boolean | undefined }>

export function buildMachine(id: string, steps: StepDefinition[], states?: any) {
  const decideTargets: Array<{ target: string; cond?: (context: FlowCtx) => boolean }> = steps.map((s) => ({
    target: s.id,
    cond: (context: FlowCtx) => context.initialState === s.id,
  }))
  decideTargets.push({ target: steps[0].id })

  const compiledStates = steps.reduce<Record<string, any>>((acc, step, i) => {
    const nextId = i < steps.length - 1 ? steps[i + 1].id : 'done'
    const prevId = i ? steps[i - 1].id : undefined

    const mergeCtxEvent = {
      MERGE_CTX: {
        actions: assign((ctx: FlowCtx, evt: any) => ({
          ...ctx,
          ...evt.payload,
        })),
      },
    }

    const always =
      step.shouldSkip != null
        ? [
            {
              target: nextId,
              cond: (context: FlowCtx) => step.shouldSkip?.(context),
            },
          ]
        : []

    let back: BackTransition | undefined = undefined
    if (step.backRules && step.backRules.length) {
      back = step.backRules.map((rule) => ({
        target: rule.target,
        cond: rule.cond ? (context: FlowCtx) => rule.cond?.(context) : undefined,
      }))
    } else if (prevId && !step.noBackButton) {
      back = prevId
    }

    const custom: Record<string, any> = {}
    if (step.customTransitions) {
      for (const [event, cfg] of Object.entries(step.customTransitions)) {
        custom[event] = {
          target: cfg.target,
          cond: cfg.cond ? (context: FlowCtx) => cfg.cond?.(context) : undefined,
        }
      }
    }

    acc[step.id] = {
      always,
      on: {
        ...mergeCtxEvent,
        RESET: {
          target: 'loading',
        },
        NEXT: {
          target: nextId,
          actions: assign((context: FlowCtx, event: any) => {
            if (event.payload) {
              return { ...context, ...event.payload }
            }
            return context
          }),
        },
        ...(back
          ? {
              BACK: Array.isArray(back)
                ? back
                : {
                    target: back,
                    actions: assign((context: FlowCtx, event: any) => {
                      if (event.payload) {
                        return { ...context, ...event.payload }
                      }
                      return context
                    }),
                  },
            }
          : {}),
        ...(step.skipAllowed
          ? {
              SKIP: {
                target: step.skipTarget ?? nextId,
                actions: assign((context: FlowCtx, event: any) => {
                  if (event.payload) {
                    return { ...context, ...event.payload }
                  }
                  return context
                }),
              },
            }
          : {}),
        ...Object.keys(custom).reduce((obj, key) => {
          obj[key] = {
            ...custom[key],
            actions: assign((context: FlowCtx, event: any) => {
              if (event.payload) {
                return { ...context, ...event.payload }
              }
              return context
            }),
          }
          return obj
        }, {} as Record<string, any>),
      },
    }

    return acc
  }, {})

  return createMachine<FlowCtx>({
    id,
    initial: 'loading',
    context: {
      skippedFrom: null,
      redirectTo: '/',
      websites: [],
      externalIntegrations: [],
      brandKitId: undefined,
      brandVisualEnhancementId: undefined,
      brandPlanId: undefined,
      language: undefined,
      brandProfileEnhancementEnabled: true,
      isBrandPlanStrategiesEnabled: false,
      isExistingUser: false,
      hasBusinessWebsite: true,
    },

    states: {
      loading: {
        on: {
          DATA_READY: {
            target: 'decideStart',
            actions: assign((context, event: any) => {
              return {
                ...context,
                ...event.payload,
              }
            }),
          },
          RESET: {
            target: 'loading',
          },
        },
      },

      decideStart: {
        always: decideTargets,
        on: {
          RESET: {
            target: 'loading',
          },
        },
      },

      ...compiledStates,
      ...states,
      done: {
        type: 'final',
        on: {
          RESET: {
            target: 'loading',
          },
        },
      },
    },
  })
}
